import React, { useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { HOSTING_PLANS, HOSTING_ADVANTAGES, HOSTING_MIGRATION_PROCESS, HOSTING_TESTIMONIAL, MAINTENANCE_PLANS } from "../mock/mock";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { Shield, Zap, Clock, Users, Plane, Activity, Server, CheckCircle2, MessageCircle, ArrowRight, Phone, Mail, Globe, ExternalLink } from "lucide-react";
import { WHATSAPP_LINK } from "../config/contact";
import { submitLead } from "../lib/lead";
import SEO from "../components/SEO";
import Header from "../components/Header";
const urlPattern = /^(https?:\/\/)?(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(\/[^\s]*)?$/i;

const schema = z.object({
  nombre: z.string().min(2, "Ingresa tu nombre"),
  email: z.string().email("Correo inválido"),
  telefono: z.string().min(7, "Teléfono inválido"),
  url: z.string().trim().optional().refine((value) => !value || urlPattern.test(value), "URL inválida"),
  mensaje: z.string().min(5, "Cuéntanos brevemente tu caso"),
  plan: z.string(),
  urgencia: z.string(),
  canal: z.string(),
  consentimiento: z.boolean().refine(v => v, { message: "Requerimos tu consentimiento" })
});

const AdvantageIcon = ({ name }) => {
  const map = { Shield, Zap, Users, Plane, Activity, Server };
  const Icon = map[name] || Server;
  return <Icon className="w-8 h-8 text-[#0a2463]" />;
};

export default function Hosting() {
  const { toast } = useToast();
  const formRef = useRef(null);
  
  const { register, handleSubmit, setValue, watch, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      nombre: "",
      email: "",
      telefono: "",
      url: "",
      mensaje: "",
      plan: "hosting",
      urgencia: "consulta",
      canal: "Email",
      consentimiento: false
    }
  });
  const consentimiento = watch("consentimiento");
  const openWhatsApp = () => window.open(WHATSAPP_LINK, "_blank", "noopener,noreferrer");

  const scrollToContact = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onSubmit = async (data) => {
    const result = await submitLead(data, "hosting-page");
    if (result.delivered) {
      toast({ 
        title: "¡Solicitud enviada!", 
        description: "Te contactaremos en breve por tu canal preferido.", 
      });
      formRef.current?.reset?.();
    } else {
      toast({ 
        title: "No pudimos enviar tu solicitud", 
        description: "Por favor contáctanos por WhatsApp para atención inmediata.", 
      });
    }
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <>
      <SEO 
        title="Hosting WordPress Administrado | Servidores Premium"
        description="Hosting WordPress premium administrado por expertos de GAREY AW. Servidores dedicados desde $199 USD/mes. Optimizado, seguro y con soporte 24/7. Infraestructura servidores.garey.mx."
        keywords="hosting wordpress, servidor wordpress, hosting administrado wordpress, servidor dedicado wordpress, hosting premium wordpress, hosting seguro"
        canonicalUrl="https://wprescate24.com/hosting-wordpress"
      />
      <div className="dark min-h-screen bg-background">
      <Header 
        currentPage="hosting"
        internalLinks={[
          { href: "#planes", label: "Planes" },
          { href: "#ventajas", label: "Ventajas" },
          { href: "#migracion", label: "Migración" }
        ]}
        ctaText="Ver Planes"
        ctaHref="#planes"
        ctaColor="blue"
      />

      <main id="main-content">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0a2463]/5 via-background to-primary/5">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="text-center max-w-5xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0a2463]/10 border border-[#0a2463]/30 mb-6">
              <Server className="w-4 h-4 text-[#0a2463]" />
              <span className="text-sm font-medium text-[#0a2463]">Hosting WordPress Administrado</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-tight">
              Hosting WordPress Administrado por <span className="text-[#0a2463]">Expertos</span> en Rendimiento y Seguridad
            </h1>
            <p className="mt-6 text-lg md:text-xl text-foreground/80 max-w-4xl mx-auto leading-relaxed">
            No es solo hosting. Es la infraestructura optimizada y gestionada por la <strong>agencia especializada</strong> que rescata y blinda sitios WordPress todos los días.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
              <Button className="bg-[#0a2463] hover:bg-[#14337f] text-white font-bold px-8 py-6 text-base" asChild>
                <a href="#planes">🚀 Ver Planes de Hosting</a>
              </Button>
            <Button variant="outline" className="border-[#0a2463] text-[#0a2463] hover:bg-[#0a2463]/5 font-semibold px-8 py-6 text-base" onClick={openWhatsApp}>
              Hablar por WhatsApp
              </Button>
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-8 text-sm text-foreground/70">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#0a2463]" /> <strong>Migración Gratuita</strong></div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#0a2463]" /> <strong>Optimizado para WP</strong></div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#0a2463]" /> <strong>Soporte Expert</strong></div>
            </div>
          </div>
        </div>
      </section>
      {/* Quick Contact Strip */}
      <section className="py-5 bg-[#0a2463] text-white">
        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 text-sm font-semibold">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              <span>Respuesta inicial en minutos</span>
            </div>
            <div className="flex items-center gap-2">
              <Server className="w-4 h-4" />
              <span>Diagnóstico técnico sin costo</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>Sin compromiso</span>
            </div>
          </div>
          <Button className="bg-white text-[#0a2463] hover:bg-white/90 font-bold" onClick={openWhatsApp}>
            <MessageCircle className="w-4 h-4 mr-2" />
            Quiero atención por WhatsApp
          </Button>
        </div>
      </section>

      {/* Ventajas Diferenciales */}
      <section id="ventajas" className="py-20 bg-background border-y border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">¿Por qué nuestro hosting es diferente?</h2>
            <p className="mt-4 text-lg text-foreground/70">No es hosting compartido común. Es infraestructura premium gestionada por especialistas WordPress</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {HOSTING_ADVANTAGES.map((advantage) => (
              <Card key={advantage.title} className="bg-gradient-to-br from-[#0a2463]/5 to-primary/5 border-2 border-border hover:border-[#0a2463]/50 transition-all hover:shadow-xl">
                <CardHeader>
                  <div className="h-16 w-16 rounded-2xl bg-[#0a2463]/10 flex items-center justify-center mb-4">
                    <AdvantageIcon name={advantage.icon} />
                  </div>
                  <CardTitle className="text-foreground text-lg font-bold">{advantage.title}</CardTitle>
                </CardHeader>
                <CardContent className="text-foreground/70 text-sm leading-relaxed">
                  {advantage.desc}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Planes de Hosting */}
      <section id="planes" className="py-20 bg-gradient-to-b from-background to-[#0a2463]/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">Planes de Hosting WordPress Administrado</h2>
            <p className="text-lg text-foreground/70 mt-4">Infraestructura premium respaldada por servidores.garey.mx</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {HOSTING_PLANS.map((plan) => (
              <Card key={plan.id} className={`relative bg-background border-2 ${plan.highlight ? "border-[#0a2463] shadow-xl shadow-[#0a2463]/10 scale-105" : "border-border"} overflow-hidden transition-all hover:shadow-2xl`}>
                {plan.highlight && (
                  <div className="absolute right-4 top-4 text-xs px-3 py-1 rounded-full bg-[#0a2463] text-white font-bold">Más Popular</div>
                )}
                <CardHeader className="space-y-4">
                  <CardTitle className="text-foreground">
                    <div className="text-2xl font-bold">{plan.name}</div>
                    {plan.customQuote ? (
                      <div className="flex flex-col items-start gap-1 mt-3">
                        <span className="text-3xl font-bold text-[#0a2463]">Cotización</span>
                        <span className="text-2xl font-bold text-[#0a2463]">Personalizada</span>
                      </div>
                    ) : (
                      <div className="flex items-baseline gap-2 mt-3">
                        <span className="text-4xl font-bold text-[#0a2463]">${plan.price}</span>
                        <span className="text-foreground/60 text-sm">{plan.priceUSD ? 'USD' : 'MXN'}/{plan.period}</span>
                      </div>
                    )}
                  </CardTitle>
                  <p className="text-sm text-foreground/70">{plan.description}</p>
                </CardHeader>
                <CardContent>
                  {/* Specs */}
                  <div className="bg-[#0a2463]/5 rounded-lg p-4 mb-6 space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-foreground/60">Rendimiento:</span>
                      <span className="font-bold text-[#0a2463]">{plan.specs.performance}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-foreground/60">Capacidad:</span>
                      <span className="font-semibold text-foreground">{plan.specs.visits}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-foreground/60">Transferencia:</span>
                      <span className="font-semibold text-foreground">{plan.specs.bandwidth}</span>
                    </div>
                  </div>
                  
                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3 text-sm text-foreground/80">
                        <CheckCircle2 className="w-5 h-5 text-[#0a2463] mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {plan.customQuote ? (
                    <Button 
                      className="w-full font-bold bg-[#0a2463] hover:bg-[#14337f] text-white"
                      onClick={scrollToContact}
                    >
                      Contactar para Cotización <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button className={`w-full font-bold ${plan.highlight ? 'bg-[#0a2463] hover:bg-[#14337f]' : 'bg-primary hover:bg-primary/90'} text-white`} asChild>
                      <a href="https://servidores.garey.mx" target="_blank" rel="noopener">
                        Contratar en servidores.garey.mx <ExternalLink className="w-4 h-4 ml-2" />
                      </a>
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-center text-sm text-foreground/60 mt-8">
            * Servicios de hosting proporcionados en colaboración con nuestra infraestructura en <a href="https://servidores.garey.mx" target="_blank" rel="noopener" className="text-[#0a2463] hover:underline font-semibold">servidores.garey.mx</a>
          </p>
        </div>
      </section>

      {/* Proceso de Migración */}
      <section id="migracion" className="py-20 bg-background">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">Proceso de Migración Sin Estrés</h2>
            <p className="text-lg text-foreground/70 mt-4">Nos encargamos de todo. Tú solo apruebas el resultado final</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6 mt-12">
            {HOSTING_MIGRATION_PROCESS.map((step) => (
              <Card key={step.step} className="bg-gradient-to-br from-[#0a2463]/5 to-primary/5 border-2 border-border hover:border-[#0a2463]/50 hover:shadow-xl transition-all relative">
                <CardHeader>
                  <div className="absolute -top-4 left-6 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#0a2463] text-white text-lg font-bold border-4 border-background">
                    {step.step}
                  </div>
                  <div className="pt-4">
                    <CardTitle className="text-foreground text-lg font-bold mb-2">{step.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="text-foreground/70 text-sm leading-relaxed">
                  {step.desc}
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button className="bg-[#0a2463] hover:bg-[#14337f] text-white font-bold px-8 py-4" asChild>
              <a href="#contacto">
                Solicitar Migración Gratuita <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonio */}
      <section className="py-20 bg-gradient-to-br from-[#0a2463]/10 via-background to-primary/5">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">Historia de Éxito</h2>
            <p className="text-lg text-foreground/70 mt-4">{HOSTING_TESTIMONIAL.highlight}</p>
          </div>
          <Card className="bg-background border-2 border-[#0a2463]/30 shadow-2xl">
            <CardContent className="p-8 md:p-12">
              <blockquote className="text-lg text-foreground/90 italic leading-relaxed mb-8">
                "{HOSTING_TESTIMONIAL.quote}"
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-[#0a2463]/10 border-2 border-[#0a2463]/30 text-[#0a2463] flex items-center justify-center text-xl font-bold">
                  {HOSTING_TESTIMONIAL.initials}
                </div>
                <div>
                  <div className="text-foreground text-lg font-bold">{HOSTING_TESTIMONIAL.name}</div>
                  <div className="text-foreground/60">{HOSTING_TESTIMONIAL.role}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Formulario de Contacto */}
      <section id="contacto" className="py-20 bg-gradient-to-b from-background to-[#0a2463]/5 border-t border-border">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">¿Listo para migrar a hosting premium?</h2>
            <p className="text-lg text-foreground/70 mt-4">Cuéntanos sobre tu sitio y te ayudaremos a elegir el plan ideal</p>
          </div>
          <div ref={formRef} className="rounded-xl bg-gradient-to-br from-[#0a2463]/5 to-primary/5 border-2 border-border p-8">
            <form className="grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="col-span-1">
                <Label htmlFor="nombre" className="text-foreground font-medium">Nombre</Label>
                <Input id="nombre" placeholder="Tu nombre" {...register("nombre")} className="bg-background border-border text-foreground placeholder:text-foreground/40" />
                {errors.nombre && <p className="text-destructive text-xs mt-1">{errors.nombre.message}</p>}
              </div>
              <div className="col-span-1">
                <Label htmlFor="email" className="text-foreground font-medium">Email</Label>
                <Input id="email" placeholder="tucorreo@ejemplo.com" {...register("email")} className="bg-background border-border text-foreground placeholder:text-foreground/40" />
                {errors.email && <p className="text-destructive text-xs mt-1">{errors.email.message}</p>}
              </div>
              <div className="col-span-1">
                <Label htmlFor="telefono" className="text-foreground font-medium">Teléfono</Label>
                <Input id="telefono" placeholder="+52 722 381 8617" {...register("telefono")} className="bg-background border-border text-foreground placeholder:text-foreground/40" />
                {errors.telefono && <p className="text-destructive text-xs mt-1">{errors.telefono.message}</p>}
              </div>
              <div className="col-span-1">
                <Label htmlFor="url" className="text-foreground font-medium">URL del sitio actual (opcional)</Label>
                <Input id="url" placeholder="tusitio.com (opcional)" {...register("url")} className="bg-background border-border text-foreground placeholder:text-foreground/40" />
                {errors.url && <p className="text-destructive text-xs mt-1">{errors.url.message}</p>}
              </div>
              <div className="col-span-1">
                <Label className="text-foreground font-medium">Servicio de interés</Label>
                <Select onValueChange={(v) => setValue("plan", v)} defaultValue="hosting">
                  <SelectTrigger className="bg-background border-border text-foreground">
                    <SelectValue placeholder="Elige un servicio" />
                  </SelectTrigger>
                  <SelectContent className="bg-background border-border text-foreground">
                    <SelectItem value="rescate">Rescate</SelectItem>
                    <SelectItem value="mantenimiento">Mantenimiento</SelectItem>
                    <SelectItem value="optimizacion">Optimización</SelectItem>
                    <SelectItem value="hosting">Hosting Profesional</SelectItem>
                  </SelectContent>
                </Select>
                {errors.plan && <p className="text-destructive text-xs mt-1">{errors.plan.message}</p>}
              </div>
              <div className="col-span-1">
                <Label className="text-foreground font-medium">Canal preferido</Label>
                <Select onValueChange={(v) => setValue("canal", v)} defaultValue="Email">
                  <SelectTrigger className="bg-background border-border text-foreground">
                    <SelectValue placeholder="¿Cómo te contactamos?" />
                  </SelectTrigger>
                  <SelectContent className="bg-background border-border text-foreground">
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                    <SelectItem value="Llamada">Llamada</SelectItem>
                  </SelectContent>
                </Select>
                {errors.canal && <p className="text-destructive text-xs mt-1">{errors.canal.message}</p>}
              </div>
              <div className="md:col-span-2 col-span-1">
                <Label htmlFor="mensaje" className="text-foreground font-medium">Cuéntanos sobre tu sitio</Label>
                <Textarea id="mensaje" rows={4} placeholder="Ej: Sitio en WooCommerce con 50,000 visitas mensuales, hosting actual en GoDaddy..." {...register("mensaje")} className="bg-background border-border text-foreground placeholder:text-foreground/40" />
                {errors.mensaje && <p className="text-destructive text-xs mt-1">{errors.mensaje.message}</p>}
              </div>
              <div className="md:col-span-2 col-span-1 flex items-center gap-2">
                <Checkbox
                  id="consentimiento"
                  checked={consentimiento}
                  onCheckedChange={(checked) => setValue("consentimiento", checked)}
                />
                <Label htmlFor="consentimiento" className="text-sm text-foreground/70">Acepto ser contactado y la política de privacidad.</Label>
              </div>
              {errors.consentimiento && <p className="md:col-span-2 col-span-1 text-destructive text-xs -mt-2">{errors.consentimiento.message}</p>}
              <div className="md:col-span-2 col-span-1">
                <Button type="submit" disabled={isSubmitting} className="w-full bg-[#0a2463] hover:bg-[#14337f] text-white font-bold py-6">
                  {isSubmitting ? "Enviando..." : "Solicitar Información y Cotización"}
                </Button>
                <p className="mt-3 text-xs text-foreground/70 text-center">Normalmente respondemos en menos de 15 minutos por WhatsApp o correo.</p>
              </div>
            </form>
            <div className="mt-4 text-xs text-foreground/60 flex items-center gap-4">
              <div className="flex items-center gap-1"><Phone className="w-4 h-4" /> Respuesta en 24h</div>
              <div className="flex items-center gap-1"><Mail className="w-4 h-4" /> Cotización personalizada</div>
              <div className="flex items-center gap-1"><Globe className="w-4 h-4" /> Sin compromiso</div>
            </div>
          </div>
          <div className="text-center mt-8">
            <p className="text-foreground/60 mb-4">¿Prefieres hablar directamente?</p>
            <Button className="bg-secondary hover:bg-secondary/90 text-white font-bold px-8 py-4" asChild>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener">
                <MessageCircle className="w-5 h-5 mr-2" />
                Contactar por WhatsApp
              </a>
            </Button>
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="bg-primary text-white">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-6 h-6 text-[#b8cd37]" />
                <span className="font-bold text-white text-lg">WPRescate24</span>
              </div>
              <p className="text-sm text-white/70">
                Agencia especializada en: optimización, rescate, seguridad y blindaje de WordPress y sitios web.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-white mb-3">Servicios</h4>
              <div className="flex flex-col gap-2">
                <RouterLink to="/" className="text-sm text-white/70 hover:text-white transition-colors">Rescate WordPress</RouterLink>
                <RouterLink to="/mantenimiento-wordpress" className="text-sm text-white/70 hover:text-white transition-colors">Mantenimiento Proactivo</RouterLink>
                <RouterLink to="/hosting-wordpress" className="text-sm text-white/70 hover:text-white transition-colors">Hosting WordPress</RouterLink>
                <RouterLink to="/blog" className="text-sm text-white/70 hover:text-white transition-colors">Blog</RouterLink>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-white mb-3">Infraestructura</h4>
              <p className="text-sm text-white/70 mb-2">Nuestros servicios están respaldados por:</p>
              <a 
                href="https://servidores.garey.mx" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#b8cd37] hover:text-[#c6da45] transition-colors"
              >
                <Server className="w-4 h-4" />
                servidores.garey.mx
              </a>
            </div>
          </div>
          <div className="border-t border-white/15 pt-6 flex flex-col md:flex-row items-center justify-between text-sm text-white/60">
            <div>© {new Date().getFullYear()} WPRescate24. Todos los derechos reservados. Una empresa de <a href="https://garey.mx" target="_blank" rel="noopener noreferrer" className="text-white hover:underline font-semibold">GAREY AW</a></div>
            <div className="flex items-center gap-6 mt-3 md:mt-0">
              <RouterLink to="/terminos" className="hover:text-white transition-colors">Términos</RouterLink>
              <RouterLink to="/privacidad" className="hover:text-white transition-colors">Privacidad</RouterLink>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener" className="hover:text-[#b8cd37] transition-colors">WhatsApp</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a href={WHATSAPP_LINK} target="_blank" rel="noopener" aria-label="Abrir WhatsApp" className="fixed bottom-6 right-6 z-50 group">
        <div className="h-16 w-16 rounded-full bg-[#25D366] hover:bg-[#20bd5a] flex items-center justify-center shadow-2xl border-2 border-white/30 transition-all hover:scale-110">
          <MessageCircle className="w-8 h-8 text-white" />
        </div>
      </a>
      </div>
    </>
  );
}
